import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import RecoveryPhrase from '@/components/RecoveryPhrase'
import PrivateKeyReveal from '@/components/PrivateKeyReveal'
import WalletManager from '@/services/wallet/wallet-manager'
import walletService from '@/services/wallet/wallet.service'
import { TriangleAlert } from 'lucide-react'

const RecoverWallet = () => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'phrase' | 'privatekey'>('phrase')
  const [mnemonic, setMnemonic] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [mismatchError, setMismatchError] = useState(false)

  useEffect(() => {
    try {
      const walletManager = WalletManager.getInstance()
      if (walletManager.isUnlocked()) {
        const wallet = walletManager.getActiveWallet()

        // Evita mostrar la frase de otra wallet si quedó data vieja desincronizada.
        const { wallets, selectedIndex } = walletService.getAllWallets()
        const selectedAddress = wallets[selectedIndex]?.address
        if (
          selectedAddress &&
          selectedAddress.toLowerCase() !== wallet.address.toLowerCase()
        ) {
          setMismatchError(true)
          return
        }

        const phrase = (wallet as any).mnemonic?.phrase || ''
        setMnemonic(phrase)
        setPrivateKey(walletManager.getPrivateKey())
      }
    } catch (e) {
      console.error('Error al obtener los datos de la billetera:', e)
    }
  }, [])

  return (
    <div className="h-full lg:w-4/5 place-self-center flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-2 w-full text-start mb-4">
        <h1 className="text-4xl font-bold text-text-main">
          {t('recover.title')}
        </h1>
        <p className="uppercase text-sm text-text-secondary tracking-[2.4px]">
          {t('recover.subtitle')}
        </p>
      </div>

      {!mismatchError && (mnemonic || privateKey) && (
        <div className="flex gap-2 bg-card rounded-full p-1 border border-border-base">
          <button
            type="button"
            onClick={() => setTab('phrase')}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
              tab === 'phrase' ? 'btn-gradient-fill' : 'text-text-secondary'
            }`}
          >
            {t('recover.tab_phrase')}
          </button>
          <button
            type="button"
            onClick={() => setTab('privatekey')}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
              tab === 'privatekey' ? 'btn-gradient-fill' : 'text-text-secondary'
            }`}
          >
            {t('recover.tab_privatekey')}
          </button>
        </div>
      )}

      {mismatchError ? (
        <div className="flex flex-col gap-4 p-6 rounded-xl border border-accent/20 bg-accent/5">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
              <TriangleAlert className="h-5 w-5 text-accent" />
            </div>
            <div className="flex flex-col gap-1.5">
              <h3 className="text-lg font-bold text-text-main">
                {t('recover.phrase_mismatch_title')}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                {t('recover.phrase_mismatch_desc')}
              </p>
            </div>
          </div>
        </div>
      ) : tab === 'phrase' ? (
        mnemonic ? (
          <RecoveryPhrase mnemonic={mnemonic} requireWarningGate />
        ) : (
          <div className="flex flex-col gap-4 p-6 rounded-xl border border-accent/20 bg-accent/5">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                <TriangleAlert className="h-5 w-5 text-accent" />
              </div>
              <div className="flex flex-col gap-1.5">
                <h3 className="text-lg font-bold text-text-main">
                  {t('recover.phrase_unavailable_title')}
                </h3>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {t('recover.phrase_unavailable_desc')}
                </p>
                <p className="text-sm text-text-secondary leading-relaxed font-semibold mt-1">
                  {t('recover.phrase_unavailable_note')}
                </p>
              </div>
            </div>
          </div>
        )
      ) : (
        <PrivateKeyReveal privateKey={privateKey} />
      )}
    </div>
  )
}

export default RecoverWallet
